import { atom } from 'jotai';
import isNil from 'lodash/isNil';
import { MetricTypes } from '@pulse/metrics';
import { ALL_TRAFFIC } from '@src/utils/filter';
import {
  findDimensionDetailsInFilter,
  getFilterModeFromDimensionTypes,
} from '@src/utils/filter/getDimensionTypesFromFilter';
import { isAppModule, CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { METRICS_TYPES_EXCLUDE_BY_FILTER } from '@utils/metrics';
import { metricsStore } from '@globalStore/metricsStore';
import dimensionStore from '@globalStore/dimensionsStore';
import type { FilterDialogRef } from '@components/filter/components/Dialog';
import type { RefObject } from 'react';

export const globalFilterAtom = atom(ALL_TRAFFIC);
export const metricsContainerRefAtom = atom<RefObject<HTMLDivElement> | undefined>(undefined);

export const pageAtom = atom((get) => {
  return {
    globalFilter: get(globalFilterAtom),
  };
});

export const filterModeAtom = atom((get) => {
  const appliedFilter = get(globalFilterAtom);
  const { dimensionByTypes } = dimensionStore.getState();
  const { dimensionTypes } = findDimensionDetailsInFilter(appliedFilter.rule, dimensionByTypes);
  const filterMode = getFilterModeFromDimensionTypes(dimensionTypes);
  return filterMode;
});

/*
For EI, filterdMetrics here, will be all EI metrics if applied filter has ad dimensions.
For AI, there are no such mapping done.
For APP, filteredMetrics is calculated based on the filter and mapping done in utils/metricList
*/
export const filterNAMetricsAtom = atom((get) => {
  const appliedFilter = get(globalFilterAtom);
  const { metricsMeta, metricsCategoryKeys } = metricsStore.getState();
  const { dimensionByTypes } = dimensionStore.getState();
  const { dimensionTypes } = findDimensionDetailsInFilter(appliedFilter.rule, dimensionByTypes);

  let excludedMetricTypes: string[] = [];
  if (isAppModule) {
    dimensionTypes.forEach((tag) => {
      excludedMetricTypes = [...excludedMetricTypes, ...METRICS_TYPES_EXCLUDE_BY_FILTER[tag]];
    });
  }
  const uniqueExcludeMetricTypes = [...new Set(excludedMetricTypes)];
  const typeGroupedMetrics = {};
  const currentMetricsMeta = metricsMeta[CURRENT_PRODUCT_MODE.toLowerCase()];
  // metricList not initialized in initial state to avoid errors or`ed with empty object
  Object.keys(currentMetricsMeta).forEach((metric: any) => {
    typeGroupedMetrics[currentMetricsMeta[metric].name] = currentMetricsMeta[metric].type;
  });
  let filteredMetrics = Object.keys(currentMetricsMeta).filter((metric) => {
    const metricType = typeGroupedMetrics[metric];
    return uniqueExcludeMetricTypes.includes(metricType);
  });

  if (isAppModule) {
    const applicableMetrics = Object.keys(currentMetricsMeta).filter((metric) => {
      const metricMeta = currentMetricsMeta[metric];

      return (
        // HP-10115, skip display rule for all flow metrics in new data model
        // should remove display rule in long term solution
        !isNil(metricMeta.flowID) ||
        // Skip the display rule for conversion and duration metrics,
        // this part of metrics has `type` "appSpecificMetrics" and `category` "ECO_USER_DEFINED_METRICS"
        (metricMeta.type === MetricTypes.appSpecificMetrics &&
          metricMeta.category.includes(metricsCategoryKeys.ECO_USER_DEFINED_METRICS))
      );
    });

    filteredMetrics = filteredMetrics.filter((metric) => !applicableMetrics.includes(metric));
  }

  return filteredMetrics;
});

export const filterCategoryTypesAtom = atom((get) => {
  const appliedFilter = get(globalFilterAtom);
  const { dimensionCategoryMap } = dimensionStore.getState();
  const { dimensionTypes } = findDimensionDetailsInFilter(appliedFilter.rule, dimensionCategoryMap);
  const dimensionCategoryTypes = [...new Set(dimensionTypes)];
  return dimensionCategoryTypes;
});

// Store the ref of the global filter dialog, will be mounted in the first time the FilterDialog is rendered.
// It's used to change the inner filter value from outside.
// Example: When the Advanced Filter is applyed, should update the inner filter value by the Advanced Filter selected value.
export const GlobalFilterRefAtom = atom<RefObject<FilterDialogRef> | undefined>(undefined);
